import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { SubscriptionService } from '../../models/subscription-service';
import { SlackStatus, TeamsStatus, AppInstallAction } from '../../proptype';
import Spinner from '../spinner/spinner';
import { ConfigContext } from "../../App";
import { URLS } from '../../constants';
import './setupchannels.scss';

export default function SetupChannels() {
    const location = useLocation<any>();
    const history = useHistory();
    const locales = useContext(ConfigContext).appConfig.languageData;

    const [slackState, setSlackState] = useState({ status: SlackStatus.None, loading: false, done: false });
    const [teamsState, setTeamsState] = useState({ status: TeamsStatus.None, loading: false, done: false });
    const [allDone, setAllDone] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    // Get actions and status from location state
    const slackAction = location.state?.slackAction || AppInstallAction.None;
    const teamsAction = location.state?.teamsAction || AppInstallAction.None;
    const slackStatus = location.state?.slackStatus || SlackStatus.None;
    const teamsStatus = location.state?.teamsStatus || TeamsStatus.None;

    useEffect(() => {
        if (!location.state) {
            history.push('/proactive-notifications/manage');
            return;
        }

        // Set initial status from passed data
        setSlackState(s => ({ ...s, status: slackStatus }));
        setTeamsState(t => ({ ...t, status: teamsStatus }));

    }, [slackStatus, teamsStatus]);

    useEffect(() => {
        // A channel is actionable if it has an action other than None
        const slackActionNeeded = slackAction !== AppInstallAction.None;
        const teamsActionNeeded = teamsAction !== AppInstallAction.None;

        // If not actionable, it's considered done; if actionable, only done when .done is true
        const slackIsDone = !slackActionNeeded || slackState.done;
        const teamsIsDone = !teamsActionNeeded || teamsState.done;

        if (slackIsDone && teamsIsDone) {
            setAllDone(true);
        }
    }, [slackState, teamsState, slackAction, teamsAction]);

    const handleSlackAction = () => {
        setSlackState(s => ({ ...s, loading: true }));
        
        if (slackAction === AppInstallAction.Revoke) {
            // Remove Slack
            const slackRemoveURL = location.state?.slackRemoveURL || URLS.REVOKE_SLACK;
            window.open(slackRemoveURL, "_blank");
            setTimeout(() => {
                setSlackState(s => ({ ...s, loading: false, done: true, status: SlackStatus.Revoked }));
            }, 5000);
        } else if (slackAction === AppInstallAction.Setup || slackAction === AppInstallAction.Modify) {
            // Install/Modify Slack
            const url = URLS.ADD_TO_SLACK_URL + "&code=" + window.adobeIMS?.getAccessToken().token;
            window.open(url, "_blank");
            setTimeout(() => {
                setSlackState(s => ({ ...s, loading: false, done: true, status: SlackStatus.Installed }));
            }, 5000);
        }
    };

    const handleTeamsAction = () => {
        setTeamsState(t => ({ ...t, loading: true }));
        
        if (teamsAction === AppInstallAction.Revoke) {
            // Remove Teams - just mark as done since subscription was already updated
            setTimeout(() => {
                setTeamsState(t => ({ ...t, loading: false, done: true, status: TeamsStatus.Revoked }));
            }, 1000);
        } else if (teamsAction === AppInstallAction.Setup || teamsAction === AppInstallAction.Modify) {
            // Install/Modify Teams - open teams-setup (it will handle missing Microsoft ID)
            const teamsSetupUrl = '/teams-setup';
            window.open(teamsSetupUrl, '_blank');
            
            setTimeout(() => {
                setTeamsState(t => ({ ...t, loading: false, done: true, status: TeamsStatus.Installed }));
            }, 5000);
        }
    };

    // Helper to get button text based on action
    function getButtonText(type: 'slack' | 'teams', action: AppInstallAction, currentStatus: SlackStatus | TeamsStatus) {
        const isSlack = type === 'slack';
        
        switch (action) {
            case AppInstallAction.Setup:
                return isSlack ? locales.setup_slack : locales.setup_teams;
            case AppInstallAction.Modify:
                return isSlack ? locales.modify_slack_setup : locales.modify_teams_setup;
            case AppInstallAction.Revoke:
                return isSlack ? locales.remove_slack : locales.remove_teams;
            default:
                return '';
        }
    }

    // Helper to get status text
    function getStatusText(action: AppInstallAction, currentStatus: SlackStatus | TeamsStatus, done: boolean) {
        if (done) {
            return locales.actioned;
        }
        
        if (action === AppInstallAction.Revoke) {
            return locales.revoke_pending;
        }
        
        if (currentStatus === SlackStatus.Installed || currentStatus === TeamsStatus.Installed) {
            return locales.installed;
        }
        
        return locales.not_installed;
    }

    const renderSection = (
        type: 'slack' | 'teams',
        state: typeof slackState | typeof teamsState,
        handler: () => void,
        action: AppInstallAction,
        currentStatus: SlackStatus | TeamsStatus
    ) => {
        // Don't show section if no action is needed
        if (action === AppInstallAction.None) {
            return null;
        }

        const title = type === 'slack' ? locales.slack : locales.teams;
        const iconClass = type === 'slack' ? 'prof-icon slack-icon' : 'prof-icon teams-icon';
        const buttonText = getButtonText(type, action, currentStatus);
        const statusText = getStatusText(action, currentStatus, state.done);
        const isActionable = !state.done;

        return (
            <div className={`channel-section channel-card ${!isActionable ? 'inactive' : ''}`}> 
                <div className="channel-row">
                    <div className="channel-info">
                        <div className="channel-title-row">
                            <i className={iconClass}></i>
                            <span className="channel-title">{title}</span>
                        </div>
                        <div className="channel-status-row">
                            <span className="status-label">{locales.installation_status}</span> 
                            <span className="channel-status">{statusText}</span>
                        </div>
                    </div>
                    {isActionable &&
                        <div>
                            <button className="btn primary continue-to-region" onClick={handler} disabled={state.loading}>
                                {buttonText}
                                {state.loading && <Spinner isButton={true} />}
                            </button>
                            <div aria-live="polite" aria-atomic="true">
                                {state.loading && <span className="sr-only">Icon indicating loading state</span>}
                            </div>
                        </div>
                    }
                </div>
            </div>
        );
    };

    return (
        <div className="setup-channels-container">
            <div className="status-stripe pns-stripe">
                <div className="container">{locales.setup_notification_channels}</div>
            </div>
            <div className="container">
                <div aria-live="assertive" aria-atomic="true">
                    {errorMessage && <div className="input-error">{errorMessage}</div>}
                </div>
                {renderSection('slack', slackState, handleSlackAction, slackAction, slackStatus)}
                {renderSection('teams', teamsState, handleTeamsAction, teamsAction, teamsStatus)}

                {allDone && (
                    <div className="success-message">
                        <h3>{locales.setup_actioned}</h3>
                        <p>{locales.complete_setup_message}</p>
                    </div>
                )}

                {/* Always show Back to Manage Subscriptions button */}
                <div className="back-to-manage">
                    <button className="btn secondary" onClick={() => history.push('/proactive-notifications/manage')}>
                        {locales.back_to_manage_subscriptions || 'Back to Manage Subscriptions'}
                    </button>
                </div>
            </div>
        </div>
    );
}
